import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { GroupLabel, KitButton, KitCard, KitList, KitPill } from '../common/MobileKit';
import MobileSheet, { SHEET_CLOSE_BUTTON_SX } from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { MobileActionItem, MobileListRow } from '../common/MobileCrud';
import { Kpi, KpiGrid, formatMoneyShort, parseMoney } from '../common/MobileDashboardKit';
import FkField from '../common/MobileFormFields';
import {
    KitChip,
    KitChips,
    KitEmpty,
    KitFab,
    KitField,
    KitFormBar,
    KitGrid2,
    KitInput,
    KitKV,
    KitSearch,
    KitSelect,
    KitSeg,
    KitStickyToolbar,
    KitSwitch,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { APP_BAR_OFFSET } from '../layout/mobileShell';
import { formatCurrency } from '../../common/formatters';

/**
 * Telas mobile do Financeiro — `prototipos-mobile/financeiro/*.html` (lançamentos, formulário
 * de lançamento, contas a pagar e a receber). Só apresentação: dados, filtros, permissões,
 * chamadas e validações vêm de `pages/transactions/*` e `pages/accounts/AccountsList.js`.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const DownLeftIcon = protoIcon('downleft');
const UpRightIcon = protoIcon('upright');
const ReceiptIcon = protoIcon('receipt');
const XIcon = protoIcon('x');

const toInput = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');
const fromInput = (text) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
};

// "DD/MM/YYYY" da API -> dayjs (ou null)
const parseApiDate = (text) => {
    const parsed = dayjs(String(text || '').slice(0, 10), 'DD/MM/YYYY', true);
    return parsed.isValid() ? parsed : null;
};

function dayLabel(day) {
    const parsed = parseApiDate(day);
    if (!parsed) return day || '—';
    const today = dayjs().startOf('day');
    if (parsed.isSame(today, 'day')) return 'Hoje';
    if (parsed.isSame(today.subtract(1, 'day'), 'day')) return 'Ontem';
    return day;
}

// `.toolbar` — busca + botão de filtros
function FinanceToolbar({ search, onSearchChange, placeholder, ariaLabel, onOpenFilters, filtersActive }) {
    return (
        <KitStickyToolbar top={APP_BAR_OFFSET}>
            <KitSearch
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={placeholder}
                inputProps={{ 'aria-label': ariaLabel || placeholder, inputMode: 'search', enterKeyHint: 'search' }}
            />
            {onOpenFilters && (
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={onOpenFilters}
                    sx={filtersActive ? { color: 'primary.main', borderColor: 'primary.main' } : undefined}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            )}
        </KitStickyToolbar>
    );
}

// Folha "Filtros" (cabeçalho + corpo + Limpar/Aplicar)
function FiltersSheet({ open, onClose, onClear, onApply, children }) {
    return (
        <MobileSheet
            open={open}
            onClose={onClose}
            title="Filtros"
            footer={(
                <>
                    <KitButton sx={{ flex: 1 }} onClick={onClear}>Limpar</KitButton>
                    <KitButton variant="primary" sx={{ flex: 1 }} onClick={onApply}>Aplicar</KitButton>
                </>
            )}
        >
            {children}
        </MobileSheet>
    );
}

function LoadingRows() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[1, 2, 3, 4].map((n) => (
                <Box key={n} sx={{ height: 60, borderRadius: '16px', backgroundColor: 'background.subtle' }} />
            ))}
        </Box>
    );
}

function RowActionsSheet({ selected, title, actions, onClose }) {
    return (
        <MobileSheet open={Boolean(selected)} onClose={onClose} title={title}>
            {actions.map((action) => (
                <MobileActionItem
                    key={action.label}
                    icon={action.icon}
                    label={action.label}
                    destructive={action.destructive}
                    chevron={!action.destructive}
                    onClick={() => {
                        const target = selected;
                        onClose();
                        action.onClick(target);
                    }}
                />
            ))}
        </MobileSheet>
    );
}

const TX_STATUS_LABELS = { pendente: 'Pendente', vencido: 'Vencido', 'prestado contas': 'Prestado Contas' };

export const TX_CHIPS = [
    { value: 'all', label: 'Todos' },
    { value: 'in', label: 'Entradas' },
    { value: 'out', label: 'Saídas' },
    { value: 'month', label: 'Este mês' },
];

/**
 * `financeiro/lancamentos.html`
 * - `getActions(row)` -> [{ label, icon, destructive, onClick }] (folha da linha)
 */
export function MobileTransactionsList({
    transactions,
    loading,
    hasMore,
    onLoadMore,
    totalRevenue,
    totalExpense,
    chip,
    onChipChange,
    search,
    onSearchChange,
    type,
    status,
    period,
    filtersActive,
    onApplyFilters,
    onClearFilters,
    canCreate,
    onNew,
    getActions,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draftStatus, setDraftStatus] = useState(status);
    const [draftStart, setDraftStart] = useState(toInput(period.start));
    const [draftEnd, setDraftEnd] = useState(toInput(period.end));
    const [selected, setSelected] = useState(null);

    const groups = useMemo(() => {
        const result = [];
        transactions.forEach((row) => {
            const day = String(row.date_created || '').slice(0, 10);
            const last = result[result.length - 1];
            if (last && last.day === day) last.items.push(row);
            else result.push({ day, items: [row] });
        });
        return result;
    }, [transactions]);

    const openFilters = () => {
        setDraftStatus(status);
        setDraftStart(toInput(period.start));
        setDraftEnd(toInput(period.end));
        setFiltersOpen(true);
    };

    const actions = selected ? getActions(selected) : [];

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <FinanceToolbar
                search={search}
                onSearchChange={onSearchChange}
                placeholder="Buscar lançamento…"
                onOpenFilters={openFilters}
                filtersActive={filtersActive}
            />

            <KitChips>
                {TX_CHIPS.map((item) => (
                    <KitChip key={item.value} active={chip === item.value} onClick={() => onChipChange(item.value)}>{item.label}</KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />
            <KpiGrid first>
                <Kpi compact label="Entradas" icon="downleft" value={formatMoneyShort(parseMoney(totalRevenue))} delta={{ tone: 'up' }} loading={loading && transactions.length === 0} />
                <Kpi compact label="Saídas" icon="upright" value={formatMoneyShort(parseMoney(totalExpense))} delta={{ tone: 'down' }} loading={loading && transactions.length === 0} />
            </KpiGrid>
            <Box sx={{ mt: '12px' }} />

            {loading && transactions.length === 0 && <LoadingRows />}

            {!loading && transactions.length === 0 && (
                <KitEmpty icon={ReceiptIcon} title="Nenhum lançamento encontrado">
                    Ajuste a busca ou os filtros para ver outros lançamentos.
                </KitEmpty>
            )}

            {groups.map((group, groupIndex) => (
                <Box key={`${group.day}-${groupIndex}`}>
                    <GroupLabel>{dayLabel(group.day)}</GroupLabel>
                    <KitList>
                        {group.items.map((row) => {
                            const isRevenue = row.type === 'receita';
                            const statusText = TX_STATUS_LABELS[String(row.status || '').toLowerCase()] || row.status;
                            return (
                                <MobileListRow
                                    key={row.id}
                                    icon={isRevenue ? DownLeftIcon : UpRightIcon}
                                    tone={isRevenue ? 'green' : 'red'}
                                    title={row.description}
                                    sub={isRevenue ? 'Receita' : `Despesa${statusText ? ` · ${statusText}` : ''}`}
                                    end={(
                                        <Box sx={{ fontWeight: 800, fontSize: 14, color: isRevenue ? 'success.main' : 'error.main' }}>
                                            {isRevenue ? '+' : '-'} R$ {formatCurrency(row.amount)}
                                        </Box>
                                    )}
                                    onClick={getActions(row).length > 0 ? () => setSelected(row) : undefined}
                                />
                            );
                        })}
                    </KitList>
                </Box>
            ))}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab label="Lançar" icon={PlusIcon} onClick={onNew} />}

            <FiltersSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                onClear={() => { setFiltersOpen(false); onClearFilters(); }}
                onApply={() => {
                    setFiltersOpen(false);
                    onApplyFilters({ status: draftStatus, start: fromInput(draftStart), end: fromInput(draftEnd) });
                }}
            >
                <KitGrid2>
                    <KitField label="Início">
                        <KitInput type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)} aria-label="Início" />
                    </KitField>
                    <KitField label="Fim">
                        <KitInput type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} aria-label="Fim" />
                    </KitField>
                </KitGrid2>
                <KitField label="Status">
                    <KitSelect value={type === 'receita' ? '' : draftStatus} onChange={(e) => setDraftStatus(e.target.value)} disabled={type === 'receita'} aria-label="Status">
                        <option value="">Todos</option>
                        <option value="pendente">Pendente</option>
                        <option value="prestado">Prestado Contas</option>
                    </KitSelect>
                </KitField>
            </FiltersSheet>

            <RowActionsSheet selected={selected} title={selected?.description || ''} actions={actions} onClose={() => setSelected(null)} />
        </Box>
    );
}

const ACCOUNT_CHIPS = (paidChip) => [
    { value: 'all', label: 'Todas' },
    { value: 'overdue', label: 'Vencidas' },
    { value: 'today', label: 'Hoje' },
    { value: 'week', label: '7 dias' },
    { value: 'paid', label: paidChip },
];

// Visão da linha: tom do ícone, texto de vencimento e pílula (protótipo `contas-pagar.html`)
function accountView(row, paidWord) {
    const shortDate = (text) => String(text || '').slice(0, 5);
    if (row.status === 'pago') {
        return { tone: 'green', sub: `${paidWord} ${shortDate(row.payment_date)}`.trim(), pill: { tone: 'success', label: paidWord } };
    }
    if (row.status === 'vencido') {
        return { tone: 'red', sub: `Venceu ${shortDate(row.due_date)}`.trim(), pill: { tone: 'danger', label: 'Vencida' } };
    }
    const due = parseApiDate(row.due_date);
    const today = dayjs().startOf('day');
    if (due && due.isSame(today, 'day')) return { tone: 'amber', sub: 'Vence hoje', pill: { tone: 'warning', label: 'Hoje' } };
    if (due && due.isSame(today.add(1, 'day'), 'day')) return { tone: 'amber', sub: 'Vence amanhã', pill: { tone: 'warning', label: 'Em breve' } };
    return { tone: undefined, sub: due ? `Vence ${shortDate(row.due_date)}` : 'Sem vencimento', pill: { tone: 'info', label: 'A vencer' } };
}

/**
 * `financeiro/contas-pagar.html` e `contas-receber.html`
 * - `kind`: 'payable' | 'receivable'
 * - `onPay(row)` abre a folha "Dar baixa" (só quando `canSettle`); `onRevert(row)` desfaz a baixa
 */
export function MobileAccountsList({
    kind,
    rows,
    loading,
    totalCount,
    hasMore,
    onLoadMore,
    totalPending,
    totalOverdue,
    chip,
    onChipChange,
    search,
    onSearchChange,
    period,
    supplierId,
    suppliers,
    filtersActive,
    onApplyFilters,
    onClearFilters,
    canCreate,
    onNew,
    canSettle,
    onPay,
    onRevert,
    payRow,
    paymentDate,
    onPaymentDateChange,
    onClosePay,
    onConfirmPay,
    payLoading,
}) {
    const isReceivable = kind === 'receivable';
    const paidWord = isReceivable ? 'Recebido' : 'Pago';
    const Icon = isReceivable ? DownLeftIcon : UpRightIcon;
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draftStart, setDraftStart] = useState(toInput(period.start));
    const [draftEnd, setDraftEnd] = useState(toInput(period.end));
    const [draftSupplier, setDraftSupplier] = useState(supplierId || '');

    const openFilters = () => {
        setDraftStart(toInput(period.start));
        setDraftEnd(toInput(period.end));
        setDraftSupplier(supplierId || '');
        setFiltersOpen(true);
    };

    const open = parseMoney(totalPending) + parseMoney(totalOverdue);
    const overdueCount = rows.filter((row) => row.status === 'vencido').length;
    let overdueText = '';
    if (!hasMore) overdueText = `${overdueCount} título${overdueCount === 1 ? '' : 's'}`;
    else if (chip === 'overdue') overdueText = `${totalCount} título${totalCount === 1 ? '' : 's'}`;

    const handleRowClick = (row) => {
        if (!canSettle) return undefined;
        if (row.status === 'pago') return () => onRevert(row);
        return () => onPay(row);
    };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <FinanceToolbar
                search={search}
                onSearchChange={onSearchChange}
                placeholder="Buscar…"
                ariaLabel={isReceivable ? 'Buscar conta a receber' : 'Buscar conta a pagar'}
                onOpenFilters={openFilters}
                filtersActive={filtersActive}
            />

            <KitChips>
                {ACCOUNT_CHIPS(isReceivable ? 'Recebidas' : 'Pagas').map((item) => (
                    <KitChip key={item.value} active={chip === item.value} onClick={() => onChipChange(item.value)}>{item.label}</KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />
            <KpiGrid first>
                <Kpi compact label="Total em aberto" icon="wallet" value={formatMoneyShort(open)} grad delta={{ tone: 'up' }} loading={loading && rows.length === 0} />
                <Kpi compact label="Vencidas" icon="alert" value={formatMoneyShort(parseMoney(totalOverdue))} delta={{ text: overdueText, tone: 'down' }} loading={loading && rows.length === 0} />
            </KpiGrid>
            <Box sx={{ mt: '12px' }} />

            {loading && rows.length === 0 && <LoadingRows />}

            {!loading && rows.length === 0 && (
                <KitEmpty icon={ReceiptIcon} title={isReceivable ? 'Nenhuma conta a receber' : 'Nenhuma conta a pagar'}>
                    Ajuste a busca ou os filtros para ver outras contas.
                </KitEmpty>
            )}

            {rows.length > 0 && (
                <KitList>
                    {rows.map((row) => {
                        const view = accountView(row, paidWord);
                        return (
                            <MobileListRow
                                key={row.id}
                                icon={Icon}
                                tone={view.tone}
                                title={isReceivable && row.customer_name ? `${row.customer_name} · ${row.description}` : row.description}
                                sub={view.sub}
                                end={(
                                    <>
                                        <Box sx={{ fontWeight: 800, fontSize: 14 }}>R$ {formatCurrency(row.amount)}</Box>
                                        <Box sx={{ mt: '3px' }}><KitPill tone={view.pill.tone}>{view.pill.label}</KitPill></Box>
                                    </>
                                )}
                                onClick={handleRowClick(row)}
                            />
                        );
                    })}
                </KitList>
            )}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab label="Nova" icon={PlusIcon} onClick={onNew} />}

            <FiltersSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                onClear={() => { setFiltersOpen(false); onClearFilters(); }}
                onApply={() => {
                    setFiltersOpen(false);
                    onApplyFilters({ start: fromInput(draftStart), end: fromInput(draftEnd), supplierId: draftSupplier });
                }}
            >
                <KitGrid2>
                    <KitField label="Vencimento de">
                        <KitInput type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)} aria-label="Vencimento de" />
                    </KitField>
                    <KitField label="Vencimento até">
                        <KitInput type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} aria-label="Vencimento até" />
                    </KitField>
                </KitGrid2>
                {!isReceivable && (
                    <KitField label="Fornecedor">
                        <KitSelect value={draftSupplier} onChange={(e) => setDraftSupplier(e.target.value)} aria-label="Fornecedor">
                            <option value="">Todos</option>
                            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </KitSelect>
                    </KitField>
                )}
            </FiltersSheet>

            <MobileSheet
                open={Boolean(payRow)}
                onClose={onClosePay}
                title="Dar baixa"
                footer={(
                    <KitButton variant="success" sx={{ flex: 1 }} onClick={onConfirmPay} disabled={payLoading}>
                        {payLoading ? 'Salvando…' : 'Confirmar baixa'}
                    </KitButton>
                )}
            >
                <KitCard sx={{ boxShadow: 'none' }}>
                    <KitKV label="Título" value={payRow?.description || '—'} />
                    <KitKV label="Valor" value={payRow ? `R$ ${formatCurrency(payRow.amount)}` : '—'} />
                </KitCard>
                <Box sx={{ mt: '12px' }} />
                <KitField label="Data do pagamento">
                    <KitInput type="date" value={paymentDate} onChange={(e) => onPaymentDateChange(e.target.value)} aria-label="Data do pagamento" />
                </KitField>
            </MobileSheet>
        </Box>
    );
}

/**
 * `financeiro/lancamento-form.html`
 * Só apresentação: `formik` é o da aba ativa (receita/despesa) de `pages/transactions/Form.js`.
 */
export function MobileTransactionForm({
    formik,
    txType,
    onTypeChange,
    isEdit,
    onCurrencyChange,
    payNowLocked,
    onSave,
    onCancel,
    loading,
    embeddedTitle,
    onClose,
}) {
    const isExpense = txType === 'despesa';
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {embeddedTitle && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', m: '-4px 0 10px' }}>
                    <Box component="h3" sx={{ m: 0, fontSize: 17, fontWeight: 800, lineHeight: 1.4 }}>{embeddedTitle}</Box>
                    <Box component="button" type="button" aria-label="Fechar" onClick={onClose} sx={SHEET_CLOSE_BUTTON_SX}>
                        <XIcon size={20} />
                    </Box>
                </Box>
            )}
            <Box sx={isEdit ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
                <KitSeg
                    options={[{ value: 'despesa', label: 'Despesa' }, { value: 'receita', label: 'Receita' }]}
                    value={txType}
                    onChange={onTypeChange}
                />
            </Box>
            {isEdit && (
                <Box sx={{ fontSize: 12, color: 'text.disabled', m: '6px 2px 0' }}>O tipo não pode ser alterado depois que a transação é criada.</Box>
            )}
            <Box sx={{ mt: '16px' }} />

            <FkField
                formik={formik}
                name="amount"
                label="Valor (R$)"
                required
                type="text"
                inputMode="numeric"
                placeholder="0,00"
                onChange={onCurrencyChange}
                onBlur={formik.handleBlur}
            />
            <FkField
                formik={formik}
                name="description"
                label="Descrição"
                required
                type="text"
                maxLength={120}
                placeholder={isExpense ? 'Ex.: Conta de luz' : 'Ex.: Venda de balcão'}
                onBlur={formik.handleBlur}
            />
            {isExpense ? (
                <KitGrid2>
                    <FkField formik={formik} name="date" label="Data" required type="date" onBlur={formik.handleBlur} />
                    <FkField formik={formik} name="dueDate" label="Vencimento" required={!isEdit} type="date" onBlur={formik.handleBlur} />
                </KitGrid2>
            ) : (
                <FkField formik={formik} name="date" label="Data" required type="date" onBlur={formik.handleBlur} />
            )}

            {isExpense && (
                <KitSwitch
                    title="Já foi pago"
                    description={payNowLocked ? 'Esta despesa já foi paga e está com prestação de contas registrada. Para reabri-la, use "Voltar para pendente" na lista.' : ''}
                    checked={Boolean(formik.values.isPay)}
                    disabled={payNowLocked}
                    onChange={(e) => formik.setFieldValue('isPay', e.target.checked)}
                />
            )}

            <KitFormBar>
                <KitButton onClick={onCancel} disabled={loading}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onSave} disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar'}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}
